import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';
import {of} from 'rxjs/internal/observable/of';
import {debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';

import {ApiError, ApiResponse, CompaniesService, HttpStatusCodesEnum, ReportsService} from '@spa/api';

import {AlertTypesEnum} from 'appShared/enums';
import {FormComponent} from 'appShared/widgets';
import {AlertsService} from 'appShared/services';
import {FormInterface} from 'appShared/interfaces';

import {
    DialogComponent,
    DialogConfigInterface,
    ReportTableConfigInterface,
    ReportTableDataRow
} from 'appShared/components';

import {SearchClientForm} from '../../forms';
import {SearchClientFormErrorStatesEnum} from '../../enums';

import {SearchClientMessages} from 'appAssets/messages';
import {
    SearchClientDataTableConfig,
    SearchClientEnrollTypeSelector,
    SearchClientNameSelector,
    SearchAliasSelector
} from 'appAssets/descriptors';

@Component({
    selector: 'app-search-client',
    templateUrl: './search-client.component.html',
    styleUrls: ['./search-client.component.scss']
})
export class SearchClientComponent extends DialogComponent<SearchClientComponent> implements FormInterface, OnDestroy {
    private getClientSubscription: Subscription;

    public searchClientForm: SearchClientForm;
    public dataTableConfig: ReportTableConfigInterface;

    constructor(
        private alertsService: AlertsService,
        private reportsService: ReportsService,
        private companiesService: CompaniesService,
        @Inject(MAT_DIALOG_DATA) public data: {
            clientType: number,
            config: DialogConfigInterface
        },
        public dialogRef: MatDialogRef<SearchClientComponent>
    ) {
        super(dialogRef);

        this.config = data.config;

        this.dataTableConfig = SearchClientDataTableConfig();

        this.initForm();
    }

    private setSearchChangedObservable(): void {
        this.getClientSubscription = this.searchClientForm.form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            map(() => this.validateForm()),
            switchMap((isValidForm: boolean) => {
                if (!isValidForm) {
                    return of(false);
                }

                return this.searchClientForm.createRequest().pipe(
                    map((response: ApiResponse<ApiError[] | ReportTableDataRow[]>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            (response.data as ApiError[]).forEach((error: ApiError) => {
                                if (error.status !== SearchClientFormErrorStatesEnum.GetClientFailed) {
                                    this.alertsService.addAlert(
                                        SearchClientMessages.UnknownError.message,
                                        AlertTypesEnum.Error
                                    );
                                } else {
                                    this.alertsService.addAlert(
                                        SearchClientMessages.GetClientsFailed.message,
                                        AlertTypesEnum.Error
                                    );
                                }
                            });
                        } else {
                            this.dataTableConfig.rows = (response.data as ReportTableDataRow[]);
                        }

                        return true;
                    })
                );
            })
        ).subscribe();
    }

    ngOnDestroy(): void {
        if (this.getClientSubscription) {
            this.getClientSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlLength);
    }

    initForm(): void {
        this.searchClientForm = new SearchClientForm(this.data.clientType, this.reportsService, this.companiesService);

        this.searchClientForm.initForm();

        this.setSearchChangedObservable();
    }

    validateForm(): boolean {
        return this.searchClientForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(rowId?: number | string): void {
        const filteredRows = this.dataTableConfig.rows.filter((row: ReportTableDataRow) => row.rowId === rowId) as ReportTableDataRow[];

        if (filteredRows.length > 0) {
            const name = filteredRows[0].getItemByColId(SearchClientNameSelector).value;
            const enrollType = filteredRows[0].getItemByColId(SearchClientEnrollTypeSelector).value;
            const alias = filteredRows[0].getItemByColId(SearchAliasSelector).value;

            this.dialogRef.close({id: rowId, name, enrollType, alias});
        } else {
            this.alertsService.addAlert(SearchClientMessages.NoDataForSelectedRow.message, AlertTypesEnum.Warning);
        }
    }
}

